import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [f, setF] = useState({
    name: "", email: "", phone: "", password: "",
    block: "", lot: "", street: "", phase: "", landmark: "", notes: "",
  });
  const [consent, setConsent] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!consent) { setErr("Please accept the Privacy Notice and Terms to continue."); return; }
    if (!/^(09|\+639)\d{9}$/.test(f.phone.replace(/\s/g, ""))) { setErr("Please enter a valid PH mobile number (e.g. 09171234567)."); return; }
    setBusy(true);
    try {
      await register({
        name: f.name, email: f.email, phone: f.phone, password: f.password,
        consent, marketing_consent: marketing,
        address: { block: f.block, lot: f.lot, street: f.street, phase: f.phase, landmark: f.landmark, notes: f.notes,
          subdivision: "Canyon Ranch Community", barangay: "Lantic", city: "Carmona", province: "Cavite" },
      });
      toast.success("Account created! Welcome to AngelSabaw.");
      nav("/");
    } catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="mb-6"><BrandLogo size="md" showTagline /></div>
        <form onSubmit={submit} className="bg-white rounded-2xl border p-6 shadow-sm" data-testid="register-form">
          <h1 className="font-display text-2xl font-bold">Create your account</h1>
          <p className="text-muted-foreground text-sm mb-5">Order in a few taps. Delivery to Canyon Ranch.</p>
          {err && <div className="bg-red-50 text-[#C62828] text-sm rounded-lg p-3 mb-4" data-testid="register-error">{err}</div>}
          <div className="grid gap-4">
            <div><Label>Full Name *</Label><Input value={f.name} onChange={set("name")} required data-testid="reg-name" className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Mobile No. *</Label><Input value={f.phone} onChange={set("phone")} placeholder="09171234567" required data-testid="reg-phone" className="mt-1" /></div>
              <div><Label>Email *</Label><Input type="email" value={f.email} onChange={set("email")} required data-testid="reg-email" className="mt-1" /></div>
            </div>
            <div><Label>Password *</Label><Input type="password" value={f.password} onChange={set("password")} required minLength={6} data-testid="reg-password" className="mt-1" /></div>

            <div className="rounded-xl bg-[#FAF8F5] border p-4">
              <div className="text-sm font-semibold text-[#1B5E20] mb-3">Delivery Address — Canyon Ranch Community, Brgy. Lantic, Carmona, Cavite</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Block</Label><Input value={f.block} onChange={set("block")} data-testid="reg-block" className="mt-1" /></div>
                <div><Label className="text-xs">Lot</Label><Input value={f.lot} onChange={set("lot")} data-testid="reg-lot" className="mt-1" /></div>
                <div><Label className="text-xs">Phase</Label><Input value={f.phase} onChange={set("phase")} className="mt-1" /></div>
                <div><Label className="text-xs">Street</Label><Input value={f.street} onChange={set("street")} data-testid="reg-street" className="mt-1" /></div>
              </div>
              <div className="mt-3"><Label className="text-xs">Landmark</Label><Input value={f.landmark} onChange={set("landmark")} className="mt-1" /></div>
              <div className="mt-3"><Label className="text-xs">Delivery Notes</Label><Textarea value={f.notes} onChange={set("notes")} className="mt-1" rows={2} /></div>
            </div>

            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox checked={consent} onCheckedChange={setConsent} data-testid="reg-consent" className="mt-0.5" />
              <span>I have read and accept the <b>Privacy Notice</b> and <b>Terms & Conditions</b>. I consent to processing of my data for account, order and delivery purposes (PH Data Privacy Act).</span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox checked={marketing} onCheckedChange={setMarketing} data-testid="reg-marketing" className="mt-0.5" />
              <span>Send me promos and updates (optional).</span>
            </label>
          </div>
          <Button type="submit" disabled={busy} data-testid="register-submit" className="w-full mt-6 h-12 bg-[#C62828] hover:bg-[#B71C1C] font-semibold">{busy ? "Creating…" : "Create Account"}</Button>
          <p className="text-sm text-center mt-4 text-muted-foreground">Have an account? <Link to="/login" className="text-[#1B5E20] font-semibold" data-testid="to-login">Log in</Link></p>
        </form>
      </div>
    </div>
  );
}
